import { useState, useEffect, useMemo } from 'react';
import { X, ChevronRight, Calendar, Search, Check, Clock, Send, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { createBoardMessage, getProperties, getUnits, getTenants, getLeads, getHousekeepers, getMaintenancePersonnel } from '@/lib/api';

const ENTITY_TYPES = [
  { value: 'property', label: 'Property' },
  { value: 'unit', label: 'Unit' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'lead', label: 'Lead' },
  { value: 'housekeeper', label: 'Housekeeper' },
  { value: 'maintenance_personnel', label: 'Maintenance' },
];

function EntityPicker({ entityType, selected, onAdd, onRemove, onDone }) {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSearch('');
    const fetchers = {
      property: () => getProperties(),
      unit: () => getUnits(),
      tenant: () => getTenants(),
      lead: () => getLeads(),
      housekeeper: () => getHousekeepers(),
      maintenance_personnel: () => getMaintenancePersonnel(),
    };
    const fn = fetchers[entityType];
    if (!fn) { setLoading(false); return; }
    fn().then(data => {
      const mapped = data.map(d => {
        let label = d.name || d.title || d.unit_number || d.id;
        if (entityType === 'unit') label = `${d.property_name || ''} ${d.unit_number || ''}`.trim() || d.id;
        return { id: d.id, label };
      });
      setOptions(mapped);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [entityType]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedIds = new Set(selected.map(s => s.id));

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input className="pl-8 h-8 text-[13px]" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 py-2">Loading...</p>
      ) : (
        <div className="max-h-[120px] overflow-y-auto border rounded-md">
          {filtered.length === 0 ? (
            <p className="text-xs text-gray-400 p-2">No results</p>
          ) : filtered.map(opt => (
            <button
              key={opt.id}
              className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-50 flex items-center justify-between border-b last:border-0 ${selectedIds.has(opt.id) ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
              onClick={() => {
                if (selectedIds.has(opt.id)) { onRemove(opt.id); }
                else { onAdd({ type: entityType, id: opt.id, label: opt.label }); onDone(); }
              }}
            >
              <span className="truncate">{opt.label}</span>
              {selectedIds.has(opt.id) && <Check className="h-3 w-3 text-teal-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function NewMessagePanel({ open, onClose, members, onCreated }) {
  const [assignedTo, setAssignedTo] = useState('');
  const [content, setContent] = useState('');
  const [delayed, setDelayed] = useState(false);
  const [postDate, setPostDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [linkedEntities, setLinkedEntities] = useState([]);
  const [activeEntityType, setActiveEntityType] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAssignedTo(''); setContent(''); setDelayed(false);
      setPostDate(''); setDueDate(''); setLinkedEntities([]);
      setActiveEntityType(''); setSaving(false);
    }
  }, [open]);

  const addEntity = (entity) => setLinkedEntities(prev => [...prev, entity]);
  const removeEntity = (id) => setLinkedEntities(prev => prev.filter(e => e.id !== id));

  const handleCreate = async () => {
    if (!assignedTo) { toast.error('Please select a team member'); return; }
    if (!content.trim()) { toast.error('Please write a message'); return; }
    if (delayed && !postDate) { toast.error('Please select a post date'); return; }
    setSaving(true);
    try {
      await createBoardMessage({
        content: content.trim(),
        assigned_to: assignedTo,
        post_date: delayed ? postDate : null,
        due_date: dueDate || null,
        linked_entities: linkedEntities,
      });
      toast.success(delayed ? 'Message scheduled' : 'Message sent');
      onCreated?.();
      onClose();
    } catch { toast.error('Failed to create message'); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-full bg-white z-50 shadow-xl flex flex-col"
        style={{ borderLeft: '0.5px solid #e5e7eb', animation: 'slideInRight 200ms ease-out' }}
        data-testid="new-message-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-[15px] font-semibold text-gray-900">New Message</h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100" data-testid="new-message-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Select team member */}
          <div>
            <Label className="text-[13px] font-medium">Send to *</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {members.map(m => (
                <button
                  key={m.id}
                  onClick={() => setAssignedTo(m.id)}
                  className={`px-3 py-2 rounded-lg border-2 text-[13px] font-medium transition-all ${
                    assignedTo === m.id ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                  data-testid={`assign-to-${m.id}`}
                >
                  {m.name}
                </button>
              ))}
            </div>
            {members.length === 0 && <p className="text-[12px] text-gray-400 mt-1">No team members. Add one first.</p>}
          </div>

          {/* Message content */}
          <div>
            <Label className="text-[13px] font-medium">Message *</Label>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write your message..."
              className="mt-1 min-h-[100px] text-[13px]"
              data-testid="message-content"
            />
          </div>

          {/* Delayed message */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[13px] font-medium flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-gray-400" /> Delayed message
              </Label>
              <Switch checked={delayed} onCheckedChange={setDelayed} data-testid="toggle-delayed" />
            </div>
            {delayed && (
              <div className="mt-2 relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input type="date" className="pl-9 h-9 text-[13px]" value={postDate} onChange={e => setPostDate(e.target.value)} data-testid="post-date" />
                <p className="text-[10px] text-gray-400 mt-1">Message will appear at 8am on this date</p>
              </div>
            )}
          </div>

          {/* Due date */}
          <div>
            <Label className="text-[13px] font-medium">Due date (optional)</Label>
            <div className="relative mt-1">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input type="date" className="pl-9 h-9 text-[13px]" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="due-date" />
            </div>
          </div>

          {/* Link entities */}
          <div>
            <Label className="text-[13px] font-medium flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-gray-400" /> Attach items (optional)
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
              {ENTITY_TYPES.map(et => (
                <button
                  key={et.value}
                  onClick={() => setActiveEntityType(activeEntityType === et.value ? '' : et.value)}
                  className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    activeEntityType === et.value ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {et.label}
                </button>
              ))}
            </div>
            {activeEntityType && (
              <EntityPicker
                entityType={activeEntityType}
                selected={linkedEntities.filter(e => e.type === activeEntityType)}
                onAdd={addEntity}
                onRemove={removeEntity}
                onDone={() => setActiveEntityType('')}
              />
            )}
            {linkedEntities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 mt-2 border-t">
                {linkedEntities.map(e => (
                  <Badge key={`${e.type}-${e.id}`} variant="secondary" className="text-[11px] gap-1 pr-1">
                    <span className="text-gray-400 capitalize">{e.type}:</span> {e.label}
                    <button onClick={() => removeEntity(e.id)} className="ml-0.5 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="px-5 py-3 border-t bg-gray-50">
          <Button
            className="w-full h-9 text-[13px] bg-teal-600 hover:bg-teal-700 gap-1.5"
            disabled={saving || !assignedTo || !content.trim()}
            onClick={handleCreate}
            data-testid="create-message-submit"
          >
            <Send className="h-3.5 w-3.5" /> {saving ? 'Sending...' : (delayed ? 'Schedule Message' : 'Send Message')}
          </Button>
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}
